import {
  deserializeWorldState,
  parseMapDefinition,
  type GamePhase,
  type WorldMapScenario,
  type WorldState,
} from "@dawn/engine";
import { defaultBrowserStorage, type StoragePort } from "./ports.ts";

/** Canonical client save slot. */
export const SAVED_GAME_STORAGE_KEY = "dawn.savedGame";

export interface WorldHostPresentationPrefs {
  foodOverlayEnabled: boolean;
  waterOverlayEnabled: boolean;
}

export const DEFAULT_WORLD_HOST_PRESENTATION_PREFS: WorldHostPresentationPrefs = {
  foodOverlayEnabled: false,
  waterOverlayEnabled: false,
};

export interface WorldSavedGameBundle {
  world: WorldState;
  presentation: WorldHostPresentationPrefs;
  mapScenario?: WorldMapScenario | null;
}

export interface WorldSavedGameSummary {
  turn: number;
  population: number;
}

export function isResumableWorldPhase(phase: GamePhase): boolean {
  return phase === "settling" || phase === "playing";
}

function resolveStorage(storage?: StoragePort | null): StoragePort | null {
  if (storage !== undefined) {
    return storage;
  }
  return defaultBrowserStorage();
}

function parsePresentationPrefs(raw: unknown): WorldHostPresentationPrefs {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_WORLD_HOST_PRESENTATION_PREFS };
  }

  const candidate = raw as Record<string, unknown>;
  return {
    foodOverlayEnabled: candidate.foodOverlayEnabled === true,
    waterOverlayEnabled: candidate.waterOverlayEnabled === true,
  };
}

function parseSavedMapScenario(raw: unknown): WorldMapScenario | null {
  if (raw === null || raw === undefined) {
    return null;
  }

  try {
    return parseMapDefinition(JSON.stringify(raw));
  } catch {
    return null;
  }
}

function parseSavedBundle(raw: unknown): WorldSavedGameBundle | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const candidate = raw as Record<string, unknown>;

  if ("world" in candidate) {
    const world = deserializeWorldState(candidate.world);
    if (world === null || !isResumableWorldPhase(world.phase)) {
      return null;
    }
    return {
      world,
      presentation: parsePresentationPrefs(candidate.presentation),
      mapScenario: parseSavedMapScenario(candidate.mapScenario),
    };
  }

  const world = deserializeWorldState(raw);
  if (world === null || !isResumableWorldPhase(world.phase)) {
    return null;
  }

  return {
    world,
    presentation: { ...DEFAULT_WORLD_HOST_PRESENTATION_PREFS },
    mapScenario: null,
  };
}

function tryParseJson(raw: string): unknown | null {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function readStoredRaw(storage: StoragePort): string | null {
  const stored = storage.getItem(SAVED_GAME_STORAGE_KEY);
  if (!stored) {
    return null;
  }

  const parsed = tryParseJson(stored);
  if (parsed === null || parseSavedBundle(parsed) === null) {
    storage.removeItem(SAVED_GAME_STORAGE_KEY);
    return null;
  }

  return stored;
}

export function getWorldSavedGameBundle(
  storage?: StoragePort | null,
): WorldSavedGameBundle | null {
  const port = resolveStorage(storage);
  if (!port) {
    return null;
  }

  try {
    const stored = readStoredRaw(port);
    if (!stored) {
      return null;
    }

    return parseSavedBundle(JSON.parse(stored));
  } catch {
    clearWorldSavedGame(port);
    return null;
  }
}

export function getWorldSavedGame(storage?: StoragePort | null): WorldState | null {
  return getWorldSavedGameBundle(storage)?.world ?? null;
}

export function getWorldSavedGameSummary(
  storage?: StoragePort | null,
): WorldSavedGameSummary | null {
  const saved = getWorldSavedGame(storage);
  if (!saved) {
    return null;
  }

  return {
    turn: saved.turn,
    population: saved.clan.population,
  };
}

export function setWorldSavedGame(
  state: WorldState,
  presentation: WorldHostPresentationPrefs = DEFAULT_WORLD_HOST_PRESENTATION_PREFS,
  mapScenario: WorldMapScenario | null = null,
  storage?: StoragePort | null,
): boolean {
  if (!isResumableWorldPhase(state.phase)) {
    return false;
  }

  const port = resolveStorage(storage);
  if (!port) {
    return false;
  }

  try {
    const payload: WorldSavedGameBundle = {
      world: state,
      presentation: {
        foodOverlayEnabled: presentation.foodOverlayEnabled === true,
        waterOverlayEnabled: presentation.waterOverlayEnabled === true,
      },
      mapScenario,
    };
    port.setItem(SAVED_GAME_STORAGE_KEY, JSON.stringify(payload));
    return true;
  } catch {
    return false;
  }
}

export function clearWorldSavedGame(storage?: StoragePort | null): void {
  const port = resolveStorage(storage);
  port?.removeItem(SAVED_GAME_STORAGE_KEY);
}
